import React, { useState } from 'react';
import { updateProfile, changePassword, errorMessage } from '../services/api';
import { useLanguage, SUPPORTED_LOCALES } from '../i18n/LanguageContext';
import { getVisibleSections } from '../menu';
import { KernelPage } from '../kernelAdminShared';
import KernelPasswordField from '../components/KernelPasswordField';
import KernelTotpSection from '../components/KernelTotpSection';

function Profile({ user, onLogout, onUserUpdate }) {
  const { t } = useLanguage();

  const [name, setName] = useState(user?.name || '');
  const [surname, setSurname] = useState(user?.surname || '');
  const [defaultPage, setDefaultPage] = useState(user?.default_page || 'dashboard');
  const [lang, setLang] = useState(user?.lang || 'it-IT');
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const availablePages = getVisibleSections(user)
    .flatMap((section) => section.items)
    .filter((item) => item.page !== 'dashboard');

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setProfileSaving(true);
    try {
      const res = await updateProfile({ name, surname, default_page: defaultPage, lang });
      if (onUserUpdate && res.user) {
        onUserUpdate(res.user);
      }
      setProfileSuccess(t('profile.profileSaved'));
    } catch (err) {
      setProfileError(errorMessage(err));
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePw = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (pw.new_password.length < 8) {
      setPwError(t('profile.passwordTooShort'));
      return;
    }
    if (!/[0-9]/.test(pw.new_password)) {
      setPwError(t('profile.passwordNeedsNumber'));
      return;
    }
    if (!/[^A-Za-z0-9]/.test(pw.new_password)) {
      setPwError(t('profile.passwordNeedsSymbol'));
      return;
    }
    if (pw.new_password !== pw.confirm) {
      setPwError(t('profile.passwordMismatch'));
      return;
    }
    setPwSaving(true);
    try {
      await changePassword(pw.current_password, pw.new_password);
      setPwSuccess(t('profile.passwordUpdated'));
      setPw({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      setPwError(errorMessage(err));
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <KernelPage user={user} onLogout={onLogout}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('profile.title')}</h1>
          <p className="page-subtitle">{t('profile.subtitle')}</p>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>{t('profile.profileTitle')}</h3>
        </div>
        <div className="card-body">
          {profileError && <div className="error-message">{profileError}</div>}
          {profileSuccess && <div className="success-message">{profileSuccess}</div>}
          <form onSubmit={handleProfileSave}>
            <div className="form-grid">
              <div className="form-group" style={{ maxWidth: 420 }}>
                <label htmlFor="profile-name">{t('profile.name')}</label>
                <input
                  id="profile-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group" style={{ maxWidth: 420 }}>
                <label htmlFor="profile-surname">{t('profile.surname')}</label>
                <input
                  id="profile-surname"
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  required
                />
              </div>
              <div className="form-group" style={{ maxWidth: 420 }}>
                <label htmlFor="profile-email">{t('profile.email')}</label>
                <input id="profile-email" type="email" value={user?.email || ''} disabled />
              </div>
              <div style={{ gridColumn: '1 / -1' }} />
              <div className="form-group" style={{ maxWidth: 420 }}>
                <label htmlFor="default-page">{t('profile.defaultPage')}</label>
                <select
                  id="default-page"
                  value={defaultPage}
                  onChange={(e) => setDefaultPage(e.target.value)}
                >
                  <option value="dashboard">{t('nav.dashboard')}</option>
                  {availablePages.map((item) => (
                    <option key={item.page} value={item.page}>{t(item.labelKey)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ maxWidth: 420 }}>
                <label htmlFor="profile-language">{t('profile.language')}</label>
                <select
                  id="profile-language"
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                >
                  {SUPPORTED_LOCALES.map((l) => (
                    <option key={l} value={l}>{t(`languages.${l}`)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn" disabled={profileSaving}>
                {profileSaving ? t('profile.saving') : t('profile.save')}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <h3>{t('profile.changePassword')}</h3>
        </div>
        <div className="card-body">
          <p className="page-subtitle" style={{ marginTop: 0 }}>{t('profile.passwordCriteria')}</p>
          {pwError && <div className="error-message">{pwError}</div>}
          {pwSuccess && <div className="success-message">{pwSuccess}</div>}
          <form onSubmit={handlePw}>
            <div className="form-grid">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>{t('profile.currentPassword')}</label>
                <input
                  type="password"
                  value={pw.current_password}
                  onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                  required
                />
              </div>
              <KernelPasswordField
                label={t('profile.newPassword')}
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                required
              />
              <KernelPasswordField
                label={t('profile.confirmNewPassword')}
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                required
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn" disabled={pwSaving}>
                {pwSaving ? t('profile.updating') : t('profile.updatePassword')}
              </button>
            </div>
          </form>
        </div>
      </div>
      <KernelTotpSection onChanged={(enabled) => onUserUpdate && onUserUpdate({ ...user, totp_enabled: enabled })} />
    </KernelPage>
  );
}

export default Profile;
